import React, { FC, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useAction, useAtom, useUpdate } from '@reatom/npm-react';
import type { Config } from '@whitesharx/starfall-frontend-chessground';
import {
  Ground,
  useGroundSet,
  usePlayerQuery,
  useGroundSize,
  useServices,
  PLAYER_COLOR,
  getTimeFormat,
  useSettingsQuery,
} from '@whitesharx/starfall-frontend-core';

import { EvaluationBar } from './components/EvaluationBar';
import { cgApiAtom, getAnalysisGameAction, rewindAction, selectedMoveAtom } from './model';
import {
  containerStyles,
  groundContainerStyles,
  evaluationBarStyles,
  groundStyles,
  enemyBarStyles,
  playerBarStyles,
  barStyles,
} from './styles.css';
import { formatePlayerData } from './utils';
import { getTimers } from './timers';
import { PlayerBar } from './components/PlayerBar';
import { AnalysisTools } from './components/AnalysisTools';

type AnalysisProps = {
  id: string;
};

const EVALUATION_BAR_SAFE_MARGIN = 15;

const Analysis: FC<AnalysisProps> = ({ id }) => {
  const { useResponsive, setMainResourcesLoaded } = useServices();
  const { isDesktop } = useResponsive();

  const { containerRef, groundSize } = useGroundSize();
  const settingsQuery = useSettingsQuery();
  const gameModesMeta = settingsQuery.data?.meta.gameModes;
  const playerQuery = usePlayerQuery();
  const playerId = playerQuery.data?.player.id;

  const [cgApi, setCgApi] = useAtom(cgApiAtom);
  const [game] = useAtom(getAnalysisGameAction.dataAtom);
  const [gameRequestStatus] = useAtom(getAnalysisGameAction.statusesAtom);
  const [selectedMove] = useAtom(selectedMoveAtom);
  const rewind = useAction(rewindAction);

  const isPlayerGame = playerId === game?.whitePlayerId || playerId === game?.blackPlayerId;
  const isWhitePlayer = !isPlayerGame || playerId === game?.whitePlayerId;

  useUpdate(getAnalysisGameAction, [id]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        rewind('previous');
      }

      if (event.key === 'ArrowUp') {
        rewind('start');
      }

      if (event.key === 'ArrowRight') {
        rewind('next');
      }

      if (event.key === 'ArrowDown') {
        rewind('end');
      }
    };

    window.addEventListener('keydown', handler);

    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, []);

  useEffect(() => {
    setMainResourcesLoaded(true);
  }, []);

  const set = useGroundSet();

  const config: Config = useMemo(
    () => ({
      viewOnly: true,
      fen: game?.startFen,
      orientation: isWhitePlayer ? 'white' : 'black',
    }),
    [game?.id, isWhitePlayer]
  );

  const timeFormat = game && getTimeFormat(game.contextType);

  const { player, enemy } = useMemo(() => {
    if (game && playerId) {
      const player = isWhitePlayer ? game.whitePlayer : game.blackPlayer;
      const enemy = isWhitePlayer ? game.blackPlayer : game.whitePlayer;
      const figuresColor = {
        player: isWhitePlayer ? PLAYER_COLOR.black : PLAYER_COLOR.white,
        enemy: isWhitePlayer ? PLAYER_COLOR.white : PLAYER_COLOR.black,
      };

      return {
        player: {
          ...formatePlayerData(player),
          color: figuresColor.player,
        },
        enemy: {
          ...formatePlayerData(enemy, player?.productId),
          color: figuresColor.enemy,
        },
      };
    }

    return { player: null, enemy: null };
  }, [game?.whitePlayer, game?.blackPlayer, isWhitePlayer, playerId]);

  const { playerLeftTime, enemyLeftTime } = useMemo(() => {
    const povPlayerId = isWhitePlayer ? game?.whitePlayerId : game?.blackPlayerId;
    return getTimers({ game, move: selectedMove, playerId: povPlayerId, meta: gameModesMeta });
  }, [selectedMove, game, isWhitePlayer, gameModesMeta]);

  const downedFigures = useMemo(() => {
    if (cgApi && selectedMove && playerId) {
      const figures = cgApi.getDownedFigures(selectedMove.fen);

      return {
        player: isWhitePlayer ? figures.w : figures.b,
        enemy: isWhitePlayer ? figures.b : figures.w,
      };
    }
  }, [selectedMove, cgApi, playerId, isWhitePlayer]);

  return (
    <>
      <div className={containerStyles} ref={containerRef}>
        <div>
          <PlayerBar
            {...enemy}
            timeFormat={timeFormat}
            leftTime={enemyLeftTime}
            className={classNames(enemyBarStyles, barStyles)}
            figures={downedFigures?.player}
          />
          <div className={groundContainerStyles}>
            <EvaluationBar className={evaluationBarStyles} move={selectedMove} />
            <Ground
              isLoading={gameRequestStatus.isPending || playerQuery.isLoading}
              config={config}
              onInit={setCgApi}
              groundSize={
                !isDesktop && typeof groundSize === 'number' ? groundSize - EVALUATION_BAR_SAFE_MARGIN : groundSize
              }
              set={set}
              className={groundStyles}
            />
          </div>
          <PlayerBar
            {...player}
            isPlayer
            timeFormat={timeFormat}
            leftTime={playerLeftTime}
            className={classNames(playerBarStyles, barStyles)}
            figures={downedFigures?.enemy}
          />
        </div>

        {!isDesktop && <AnalysisTools />}
      </div>
    </>
  );
};

export default Analysis;
